import { useEffect, useMemo, useRef, useState } from "react";
import {
  evolutionChartScale,
  seasonEndYear,
  yearTickIndices,
} from "../helpers/leagueEvolutionChart";
export default function LeagueEvolutionChart({
  series,
  reference,
  label,
  format,
  t,
  summary,
  metric,
  locale = "de",
}) {
  const [selected, setSelected] = useState(null),
    [showReference, setShowReference] = useState(false);
  const container = useRef(null),
    [width, setWidth] = useState(900);
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) =>
      setWidth(Math.max(240, entries[0].contentRect.width))
    );
    if (container.current) observer.observe(container.current);
    return () => observer.disconnect();
  }, []);
  // Always include the comparison in the domain: toggling its visibility never moves the league curve.
  const scale = useMemo(
    () =>
      evolutionChartScale(
        series,
        [...reference, { value: summary?.long_term?.value }],
        metric
      ),
    [series, reference, metric, summary]
  );
  const tickIndices = new Set(yearTickIndices(series, width));
  const tickFormat = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2,
  });
  if (
    !series.some((p) => p.value != null) &&
    !reference.some((p) => p.value != null)
  )
    return <p>{t("empty")}</p>;
  const w = Math.max(320, width),
    left = 65,
    right = w - 15,
    top = 25,
    bottom = 215;
  const x = (i) => left + (i * (right - left)) / Math.max(1, series.length - 1);
  const y = (v) =>
    bottom -
    ((v * scale.factor - scale.minimum) * (bottom - top)) /
      (scale.maximum - scale.minimum);
  function path(points) {
    return points
      .map((p, i) =>
        p.value == null
          ? ""
          : `${i === 0 || points[i - 1].value == null ? "M" : "L"}${x(i)},${y(
              p.value
            )}`
      )
      .join(" ");
  }
  const exact = (v) =>
    v == null
      ? "—"
      : new Intl.NumberFormat(locale, { maximumSignificantDigits: 15 }).format(
          metric === "draw" ? v * 100 : v
        ) + (metric === "draw" ? " %" : "");
  const point = selected == null ? null : series[selected];
  return (
    <section ref={container} className="le-chart" aria-label={t("trend")}>
      <h3>{label}</h3>
      <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          style={{ width: "auto", margin: 0 }}
          type="checkbox"
          checked={showReference}
          onChange={(e) => setShowReference(e.target.checked)}
        />
        {t("reference")}
      </label>
      <svg
        viewBox={`0 0 ${w} 260`}
        role="img"
        aria-label={label}
        style={{ width: "100%", maxHeight: 300 }}
      >
        <line
          x1={left}
          x2={left}
          y1={top}
          y2={bottom}
          stroke="currentColor"
          opacity=".5"
        />
        {scale.ticks.map((value) => {
          const yy = y(value / scale.factor);
          return (
            <g key={value} className="le-y-tick">
              <line
                x1={left - 4}
                x2={right}
                y1={yy}
                y2={yy}
                stroke="currentColor"
                opacity=".12"
              />
              <text
                x={left - 9}
                y={yy + 4}
                textAnchor="end"
                fill="currentColor"
                fontSize="12"
              >
                {tickFormat.format(value)}
                {metric === "draw" ? " %" : ""}
              </text>
            </g>
          );
        })}
        {summary?.long_term?.value != null && (
          <line
            className="le-long-term"
            x1={left}
            x2={right}
            y1={y(summary.long_term.value)}
            y2={y(summary.long_term.value)}
            stroke="#9bbaad"
            strokeWidth="1"
            strokeDasharray="2 4"
          >
            <title>
              {t("long")} · {label}: {exact(summary.long_term.value)}
            </title>
          </line>
        )}
        {summary?.long_term?.value != null && (
          <line
            x1={left}
            x2={right}
            y1={y(summary.long_term.value)}
            y2={y(summary.long_term.value)}
            stroke="transparent"
            strokeWidth="12"
          >
            <title>
              {t("long")} · {label}: {exact(summary.long_term.value)}
            </title>
          </line>
        )}
        {showReference && (
          <path
            className="le-reference-line"
            d={path(reference)}
            fill="none"
            stroke="#aaa"
            strokeWidth="2"
            strokeDasharray="5 4"
          >
            <title>
              {t("reference")} · {t(metric)}
            </title>
          </path>
        )}
        {showReference &&
          reference.map((p, i) =>
            p.value == null ? null : (
              <circle
                key={`ref-${i}`}
                cx={x(i)}
                cy={y(p.value)}
                r="9"
                fill="transparent"
                stroke="none"
              >
                <title>
                  {t("reference")} · {p.season} · {t(metric)}: {exact(p.value)}
                </title>
              </circle>
            )
          )}
        <path
          className="le-league-line"
          d={path(series)}
          fill="none"
          stroke="#5cb8ed"
          strokeWidth="3"
        />
        {series.map((p, i) => {
          const extreme =
            p.value != null &&
            (p.value === summary?.minimum || p.value === summary?.maximum);
          return (
            <g key={p.season}>
              {tickIndices.has(i) && (
                <text
                  className="le-x-tick"
                  x={x(i)}
                  y="245"
                  textAnchor="middle"
                  fill="currentColor"
                  fontSize="12"
                >
                  {seasonEndYear(p.season)}
                </text>
              )}
              {p.value != null && (
                <circle
                  cx={x(i)}
                  cy={y(p.value)}
                  r={extreme ? 5 : 3.5}
                  fill="#5cb8ed"
                  stroke={extreme ? "#e1edf5" : "none"}
                  pointerEvents="none"
                />
              )}
              {p.value != null && (
                <circle
                  cx={x(i)}
                  cy={y(p.value)}
                  r={9}
                  fill="transparent"
                  stroke="none"
                  tabIndex="0"
                  role="button"
                  aria-label={`${label} · ${p.season}: ${exact(p.value)}`}
                  onClick={() => setSelected(i)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelected(i);
                    }
                  }}
                >
                  <title>
                    {label} · {p.season}: {exact(p.value)}
                    {extreme
                      ? ` · ${t(
                          p.value === summary.minimum ? "minimum" : "maximum"
                        )}`
                      : ""}
                  </title>
                </circle>
              )}
            </g>
          );
        })}
      </svg>
      {summary && (
        <p className="le-note le-chart-legend">
          {t("long")}: {format(summary.long_term.value)} · {t("minimum")}:{" "}
          {format(summary.minimum)} (
          {summary.minimum_seasons.map(seasonEndYear).join(", ") || "—"}) ·{" "}
          {t("maximum")}: {format(summary.maximum)} (
          {summary.maximum_seasons.map(seasonEndYear).join(", ") || "—"})
        </p>
      )}
      {point && (
        <p role="status">
          {point.season}: {format(point.value)} ·{" "}
          {point.teams != null
            ? t("teamSample", { count: point.teams })
            : t("sample", { count: point.games })}
        </p>
      )}
    </section>
  );
}
